/**
 * JSON:API Query Parameters Builder
 *
 * Centralized utility for building URL query strings following JSON:API conventions.
 * Handles common patterns: page[number], page[size], filter[key], include, sort.
 *
 * @example
 * buildQueryString({
 *   page: { number: 2, size: 10 },
 *   filter: { status: 'active', type: 'render' },
 *   sort: '-created_at',
 *   include: 'user,project'
 * })
 * // → 'page[number]=2&page[size]=10&filter[status]=active&filter[type]=render&sort=-created_at&include=user,project'
 */

/**
 * Allowed filter value types
 */
export type FilterValue =
  | string
  | number
  | boolean
  | string[]
  | undefined
  | null;

export interface QueryParams {
  /** Pagination: { number, size } → page[number], page[size] */
  page?: {
    number?: number;
    size?: number;
  };
  /** Filters: { key: value } → filter[key]=value */
  filter?: Record<string, FilterValue>;
  /** Sort field(s) - passed as-is */
  sort?: string | string[];
  /** Include related resources - array joined by comma or string */
  include?: string | string[];
  /** Any additional simple params */
  [key: string]: unknown;
}

/**
 * Build URL query string from JSON:API style parameters
 *
 * @param params - Query parameters object
 * @returns URL-encoded query string (without leading '?')
 */
export function buildQueryString(params: QueryParams): string {
  const searchParams = new URLSearchParams();

  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null) continue;

    switch (key) {
      case 'page':
        // Handle page[number] and page[size]
        if (typeof value === 'object' && value !== null) {
          const pageParams = value as { number?: number; size?: number };
          if (pageParams.number !== undefined) {
            searchParams.append('page[number]', String(pageParams.number));
          }
          if (pageParams.size !== undefined) {
            searchParams.append('page[size]', String(pageParams.size));
          }
        }
        break;

      case 'filter':
        // Handle filter[key]=value
        if (typeof value === 'object' && value !== null) {
          for (const [filterKey, filterValue] of Object.entries(
            value as Record<string, unknown>
          )) {
            if (filterValue === undefined || filterValue === null) continue;
            if (Array.isArray(filterValue)) {
              // Arrays are joined by comma: filter[status]=pending,completed
              searchParams.append(
                `filter[${filterKey}]`,
                filterValue.join(',')
              );
            } else {
              searchParams.append(`filter[${filterKey}]`, String(filterValue));
            }
          }
        }
        break;

      case 'sort':
        // Sort can be string or array
        if (Array.isArray(value)) {
          searchParams.append('sort', value.join(','));
        } else if (typeof value === 'string') {
          searchParams.append('sort', value);
        }
        break;

      case 'include':
        // Include can be string or array
        if (Array.isArray(value)) {
          searchParams.append('include', value.join(','));
        } else if (typeof value === 'string') {
          searchParams.append('include', value);
        }
        break;

      default:
        // Handle other simple params
        if (typeof value === 'object' && value !== null) {
          // Nested objects: key[subKey]=value
          for (const [subKey, subValue] of Object.entries(
            value as Record<string, unknown>
          )) {
            if (subValue !== undefined && subValue !== null) {
              if (Array.isArray(subValue)) {
                subValue.forEach((v) =>
                  searchParams.append(`${key}[${subKey}][]`, String(v))
                );
              } else {
                searchParams.append(`${key}[${subKey}]`, String(subValue));
              }
            }
          }
        } else if (Array.isArray(value)) {
          // Arrays: key[]=value
          value.forEach((v) => searchParams.append(`${key}[]`, String(v)));
        } else {
          // Simple values
          searchParams.append(key, String(value));
        }
    }
  }

  return searchParams.toString();
}

/**
 * Build URL with query string
 *
 * @param baseUrl - Base URL without query string
 * @param params - Query parameters
 * @returns Full URL with query string
 */
export function buildUrl(baseUrl: string, params?: QueryParams): string {
  if (!params) return baseUrl;
  const queryString = buildQueryString(params);
  return queryString ? `${baseUrl}?${queryString}` : baseUrl;
}
